import React, { useState, useEffect, useCallback } from 'react';
import { 
    Modal, 
    Input, 
    Select, 
    Table, 
    Tag, 
    Space, 
    Typography,
    Button,
    Row,
    Col,
    Tooltip
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import useGroupsStore from '../../stores/useGroupsStore';
import apiCore from '../../utils/api-core';
import session from '../../utils/session';
import _ from 'lodash';
import formatterUtil from '../../utils/format';

const { Text } = Typography;
const { Option } = Select;

const AddContactsModal = ({ 
    visible, 
    onCancel, 
    onAdd, // Function(selectedContactObjects)
    excludeContactIds = [], // IDs to disable or hide? For now maybe just ignore.
    confirmLoading = false
}) => {
    const { 
        fetchCompanyContacts, 
        companyContacts, 
        totalContacts,
        loading: storeLoading 
    } = useGroupsStore();

    const [existingContactsSearch, setExistingContactsSearch] = useState('');
    const [selectedExistingKeys, setSelectedExistingKeys] = useState([]);
    // We keep track of selected objects to ensure we don't lose them if the list changes
    const [selectedContactsMap, setSelectedContactsMap] = useState(new Map());
    
    const [existingContactsPage, setExistingContactsPage] = useState(1);
    const [advancedFilters, setAdvancedFilters] = useState({
        email: '',
        mobile: '',
        tags: [],
        groups: []
    });
    const [companyTags, setCompanyTags] = useState([]);
    const [availableGroups, setAvailableGroups] = useState([]); // If we want to filter by groups, we need the list of groups

    // Load initial data when modal opens
    useEffect(() => {
        if (visible) {
            fetchTags();
            fetchGroupsList(); // We might need a separate call or use store if available, to not conflict
            loadContacts(1, '');
            // Reset selection or keep it? usually reset on open unless controlled
            setSelectedExistingKeys([]);
            setSelectedContactsMap(new Map());
            setExistingContactsSearch('');
            setAdvancedFilters({ email: '', mobile: '', tags: [], groups: [] });
            setExistingContactsPage(1);
        }
    }, [visible]);

    const fetchTags = async () => {
        try {
            const userData = session.get('userData') || {};
            const companyId = userData.companyId; 
            if (companyId) {
                const res = await apiCore.get(`/api/v3/company/${companyId}/tags`);
                const tags = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
                setCompanyTags(tags);
            }
        } catch (error) {
            console.error('Failed to fetch tags', error);
        }
    };

    // We can use useGroupsStore().fetchGroups but that modifies the main groups list.
    // Ideally we want a lightweight list for the dropdown. 
    // For now, let's reuse apiCore call to avoid messing with store state if this component is used inside ViewGroup
    const fetchGroupsList = async () => {
        try {
            const userData = session.get('userData') || {};
            const companyId = userData.companyId; 
            if (companyId) {
                // Determine API endpoint. 
                // Creating a simplified custom call or relying on store is options.
                // Let's use the store's getter logic but via direct API to contain side effects?
                // Actually, useGroupsStore fetchGroups is fine if we are aware it updates 'groups' in store.
                // But `CreateGroupV2` uses `groups` from store for the dropdown too.
                // Let's just use the API directly to be safe and reusable.
                const res = await apiCore.get(`/api/v3/groups/company/${companyId}`);
                const groupsData = res.data.data || res.data || [];
                setAvailableGroups(groupsData);
            }
        } catch (error) {
            console.error('Failed to fetch groups', error);
        }
    };

    const loadContacts = async (pageNum, search, filters = {}) => {
        const userData = session.get('userData') || {};
        const companyId = userData.companyId;
        if (!companyId) return;

        const params = {
            page: pageNum,
            pageSize: 50,
            search: search,
        };
        
        if (filters.tags && filters.tags.length > 0) {
            params.tags = filters.tags;
        }
        if (filters.groups && filters.groups.length > 0) {
            params.groups = filters.groups;
        }
        
        try {
            await fetchCompanyContacts(companyId, params, { replace: true });
        } catch (e) {
            console.error(e);
        }
    };


    const handleExistingSearch = (value) => {
        setExistingContactsSearch(value);
        setExistingContactsPage(1);
        loadContacts(1, value, advancedFilters);
    };
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedExistingSearch = useCallback(_.debounce(handleExistingSearch, 500), [advancedFilters]);

    const handleTableChange = (pagination) => {
        setExistingContactsPage(pagination.current);
        loadContacts(pagination.current, existingContactsSearch, advancedFilters);
    };

    const handleSelectionChange = (keys, selectedRows) => {
        setSelectedExistingKeys(keys);
        
        // Update our map of selected objects
        const newMap = new Map(selectedContactsMap);
        
        // Add currently selected rows to map
        selectedRows.forEach(row => {
            newMap.set(row.id, row);
        });

        // If any keys were unselected, remove them
        // (This is tricky with infinite scroll if row is not in current view)
        // With Antd Table preserveSelectedRowKeys: true, we might get all keys but not all rows.
        // But here we are handling `selectedRows` which only contains rows in current page usually?
        // Antd Table `onChange` gives `selectedRows` for current page data? 
        // Actually for server side paging, we usually only get rows available in data.
        
        // Simplest approach: We trust `keys`. We add any *new* rows we see to our map.
        // We don't remove based on missing rows in `selectedRows` if they are not in current view.
        // But how do we know if user *unselected* a row that is not in view?
        // Antd `rowSelection.onChange` returns `selectedRowKeys`. 
        // Depending on `preserveSelectedRowKeys`, we might need to manage this carefully.
        
        // Let's rely on basic behavior: 
        // When selection changes, if we have the object in `selectedRows` (which comes from `companyContacts`), update map.
        // If we want to support unselecting: we need to sync keys with map.
        
        // For now, let's just assume `selectedRows` covers what we just clicked roughly. 
        // More robust: Iterate `companyContacts` (current view). If id in `keys`, add to map. If not in `keys` AND in `companyContacts`, remove from map.
        
        const currentViewIds = companyContacts.map(c => c.id);
        
        currentViewIds.forEach(id => {
            if (keys.includes(id)) {
                const contact = companyContacts.find(c => c.id === id);
                if (contact) newMap.set(id, contact);
            } else {
                if (newMap.has(id)) newMap.delete(id);
            }
        });
        
        setSelectedContactsMap(newMap);
    };

    const handleAdd = () => {
        // Collect all objects from map
        const selectedObjects = Array.from(selectedContactsMap.values());
        
        // Also safeguard: if map missed something but keys have it and it's in current view?
        // (Should be covered by handleSelectionChange logic)
        
        // Fallback: filter from map based on final keys
        const finalObjects = selectedObjects.filter(c => selectedExistingKeys.includes(c.id));
        
        onAdd(finalObjects);
    };

    // Columns
    const existingColumns = [
        {
            title: 'First Name',
            key: 'firstName',
            render: (text, record) => <Text>{record.name ? record.name.split(' ')[0] : '-'}</Text>
        },
        {
            title: 'Last Name',
            key: 'lastName',
            render: (text, record) => <Text>{record.name ? record.name.split(' ').slice(1).join(' ') : '-'}</Text>
        },
        {
            title: 'Mobile',
            dataIndex: 'mobile',
            key: 'mobile',
            render: (mobile) => mobile ? formatterUtil.forceFormat(mobile) : '-'
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Groups',
            dataIndex: 'groups',
            key: 'groups',
            width: 200,
            render: groups => {
                if (!groups || groups.length === 0) return '-';
                const first = groups[0];
                const restCount = groups.length - 1;
                return (
                    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                         <Tag style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {first.name}
                        </Tag>
                        {restCount > 0 && (
                            <Tooltip title={groups.slice(1).map(g => g.name).join(', ')}>
                                <Tag>+{restCount}</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
         {
            title: 'Tags',
            dataIndex: 'tags',
            key: 'tags',
            width: 200,
            render: tags => {
                if (!tags || tags.length === 0) return '-';
                const first = tags[0];
                const restCount = tags.length - 1;
                return (
                    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center' }}>
                         <Tag color="blue" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {first}
                        </Tag>
                        {restCount > 0 && (
                            <Tooltip title={tags.slice(1).join(', ')}>
                                <Tag>+{restCount}</Tag>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        }
    ];

    return (
        <Modal
            title="Add Existing Contacts"
            visible={visible}
            onCancel={onCancel}
            onOk={handleAdd}
            confirmLoading={confirmLoading}
            width={1000}
            style={{ top: 20 }}
            okText={`Add Selected (${selectedExistingKeys.length})`}
        >
            <div style={{ marginBottom: 12}}>
                <Row gutter={[16, 16]}>
                    <Col span={8}>
                        <Input 
                            placeholder="Search by Name, Mobile, Email..." 
                            prefix={<SearchOutlined />} 
                            onChange={(e) => debouncedExistingSearch(e.target.value)}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={8}>
                        <Select
                            mode="multiple"
                            placeholder="Filter by Tags"
                            style={{ width: '100%' }}
                            onChange={(val) => {
                                setAdvancedFilters(prev => ({ ...prev, tags: val }));
                                loadContacts(1, existingContactsSearch, { ...advancedFilters, tags: val });
                            }}
                            value={advancedFilters.tags}
                            maxTagCount="responsive"
                        >
                            {Array.isArray(companyTags) && companyTags.map(tag => <Option key={tag} value={tag}>{tag}</Option>)}
                        </Select>
                    </Col>
                    <Col span={8}>
                         <Select
                            mode="multiple"
                            placeholder="Filter by Groups"
                            style={{ width: '100%' }}
                            onChange={(val) => {
                                setAdvancedFilters(prev => ({ ...prev, groups: val }));
                                loadContacts(1, existingContactsSearch, { ...advancedFilters, groups: val });
                            }}
                             value={advancedFilters.groups}
                             maxTagCount="responsive"
                        >
                            {Array.isArray(availableGroups) && availableGroups.map(g => <Option key={g.id} value={g.id}>{g.name}</Option>)}
                        </Select>
                    </Col>
                </Row>
            </div>
            
            <Table 
                dataSource={companyContacts} 
                columns={existingColumns} 
                rowKey="id" 
                pagination={{
                    current: existingContactsPage,
                    pageSize: 50,
                    total: totalContacts,
                    showSizeChanger: false,
                    position: ['bottomCenter']
                }}
                onChange={handleTableChange}
                loading={storeLoading}
                size="middle"
                scroll={{ y: 'calc(80vh - 250px)' }}
                rowSelection={{
                    selectedRowKeys: selectedExistingKeys,
                    onChange: handleSelectionChange,
                    preserveSelectedRowKeys: true 
                }}
            />
            
        </Modal>
    );
};

export default AddContactsModal;
